import React, { useState, useEffect } from "react";

import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const STAGE_TYPES = [
  { value: "open", label: "Aberto" },
  { value: "won", label: "Ganho" },
  { value: "lost", label: "Perdido" },
];

const DEFAULT_COLOR = "#9E9E9E";

const useStyles = makeStyles((theme) => ({
  stageRow: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  },
  colorInput: {
    width: 40,
    height: 40,
    padding: 0,
    border: "none",
    background: "none",
    cursor: "pointer",
  },
  sectionHint: {
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
    marginBottom: theme.spacing(1),
  },
}));

// chave estável para raias ainda não salvas (o índice muda ao reordenar)
let tempKeySeq = 0;
const nextTempKey = () => {
  tempKeySeq += 1;
  return `novo-${tempKeySeq}`;
};

const toFormStage = (stage) => ({
  key: String(stage.id),
  id: stage.id,
  name: stage.name || "",
  color: stage.color || DEFAULT_COLOR,
  type: stage.type || "open",
  taskTemplate: stage.taskTemplate || "",
  taskDueDays: stage.taskDueDays == null ? "" : String(stage.taskDueDays),
});

const PipelineModal = ({ open, onClose, pipeline }) => {
  const classes = useStyles();

  const [name, setName] = useState("");
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !pipeline) return;
    setName(pipeline.name || "");
    setStages(
      (pipeline.stages || [])
        .slice()
        .sort((a, b) => a.order - b.order)
        .map(toFormStage)
    );
  }, [open, pipeline]);

  const updateStage = (key, field) => (e) => {
    const { value } = e.target;
    setStages((prev) =>
      prev.map((stage) =>
        stage.key === key ? { ...stage, [field]: value } : stage
      )
    );
  };

  const addStage = () => {
    setStages((prev) => [
      ...prev,
      {
        key: nextTempKey(),
        name: "",
        color: DEFAULT_COLOR,
        type: "open",
        taskTemplate: "",
        taskDueDays: "",
      },
    ]);
  };

  const removeStage = (key) => {
    setStages((prev) => prev.filter((stage) => stage.key !== key));
  };

  const moveStage = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= stages.length) return;
    setStages((prev) => {
      const next = prev.slice();
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Informe o nome do funil.");
      return;
    }
    if (stages.length === 0) {
      toast.error("O funil precisa de pelo menos uma etapa.");
      return;
    }
    if (stages.some((stage) => !stage.name.trim())) {
      toast.error("Todas as etapas precisam de um nome.");
      return;
    }
    if (!stages.some((stage) => stage.type === "open")) {
      toast.error(
        "O funil precisa de pelo menos uma etapa do tipo Aberto para receber novos negócios."
      );
      return;
    }

    setLoading(true);
    try {
      // a API trata a lista como a verdade do funil: etapa existente que
      // não vier aqui é excluída, por isso enviamos sempre tudo
      const payload = {
        name: name.trim(),
        stages: stages.map((stage) => ({
          id: stage.id,
          name: stage.name.trim(),
          color: stage.color,
          type: stage.type,
          taskTemplate: stage.taskTemplate.trim() || null,
          taskDueDays: stage.taskDueDays === "" ? null : Number(stage.taskDueDays),
        })),
      };

      await api.put(`/pipelines/${pipeline.id}`, payload);
      toast.success("Funil atualizado.");
      onClose(true);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose()} maxWidth="md" fullWidth>
      <DialogTitle>Configurar funil</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Nome do funil"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              variant="outlined"
              size="small"
            />
          </Grid>

          <Grid item xs={12}>
            <Alert severity="info">
              As etapas aparecem no board na ordem desta lista. Etapas do tipo{" "}
              <strong>Ganho</strong> e <strong>Perdido</strong> fecham o negócio
              — use <strong>Ganho</strong> para etapas de pós-venda, assim a
              venda continua contando no relatório. Uma etapa só pode ser
              excluída se não tiver nenhum negócio.
            </Alert>
          </Grid>

          <Grid item xs={12}>
            <Typography className={classes.sectionHint}>
              Etapas do funil
            </Typography>

            {stages.map((stage, index) => (
              <div key={stage.key} className={classes.stageRow}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Nome da etapa"
                      value={stage.name}
                      onChange={updateStage(stage.key, "name")}
                      fullWidth
                      variant="outlined"
                      size="small"
                      placeholder="Ex: Pós-venda"
                    />
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <FormControl fullWidth variant="outlined" size="small">
                      <InputLabel>Tipo</InputLabel>
                      <Select
                        value={stage.type}
                        onChange={updateStage(stage.key, "type")}
                        label="Tipo"
                      >
                        {STAGE_TYPES.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={3} sm={2}>
                    <Tooltip title="Cor da etapa">
                      <input
                        type="color"
                        className={classes.colorInput}
                        value={stage.color}
                        onChange={updateStage(stage.key, "color")}
                      />
                    </Tooltip>
                  </Grid>

                  <Grid item xs={3} sm={3} style={{ textAlign: "right" }}>
                    <Tooltip title="Mover para cima">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => moveStage(index, -1)}
                          disabled={index === 0}
                        >
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Mover para baixo">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => moveStage(index, 1)}
                          disabled={index === stages.length - 1}
                        >
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Remover etapa">
                      <IconButton
                        size="small"
                        onClick={() => removeStage(stage.key)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Grid>

                  <Grid item xs={12} sm={8}>
                    <TextField
                      label="Tarefa automática ao entrar nesta etapa"
                      value={stage.taskTemplate}
                      onChange={updateStage(stage.key, "taskTemplate")}
                      fullWidth
                      variant="outlined"
                      size="small"
                      placeholder="Ex: Entrar em contato com {contato}"
                      helperText="Vazio = não gera tarefa. Variáveis: {contato} e {negocio}."
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Prazo da tarefa (dias)"
                      type="number"
                      value={stage.taskDueDays}
                      onChange={updateStage(stage.key, "taskDueDays")}
                      fullWidth
                      variant="outlined"
                      size="small"
                      inputProps={{ min: 0 }}
                      helperText="Vazio = sem prazo."
                    />
                  </Grid>
                </Grid>
              </div>
            ))}

            <Button
              startIcon={<AddIcon />}
              onClick={addStage}
              color="primary"
              size="small"
            >
              Adicionar etapa
            </Button>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose()} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PipelineModal;
